import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { CalculationFormSidebarStatusEnum } from './calculation-form-sidebar-status.enum';
import { Heading } from './calculation-form-sidebar-heading/calculation-form-sidebar-heading.type';
import { FormChangeManager } from '../../managers/form-change.manager';
import { Subscription } from 'rxjs';
import { AdvancedForm } from "@magieno/angular-advanced-forms";
import { CalculationStore } from '../../stores/calculation.store';
import { ActivatedRoute } from '@angular/router';
import { CalculationFormSidebarViewEnum } from './calculation-form-sidebar-view.enum';
import { LocaleService } from '../../../advanced-auth/services/locale.service';
import { Locale } from '@magieno/common';
import { DebugUtils } from '../../../core/utils/debug.utils';
import { EventManager } from '../../../core/managers/event.manager';
import { CalculationEventEnum } from '../../../core/enums/calculation-event.enum';

@Component({
  selector: 'app-calculation-form-sidebar',
  templateUrl: './calculation-form-sidebar.component.html',
  styleUrls: ['./calculation-form-sidebar.component.scss'],
  standalone: false,
})
export class CalculationFormSidebarComponent implements OnInit, OnDestroy {

  status: CalculationFormSidebarStatusEnum = CalculationFormSidebarStatusEnum.Open;

  headings: Heading[] = [];

  formChangeSubscription: Subscription;

  calculationSubscription: Subscription;

  view: CalculationFormSidebarViewEnum = CalculationFormSidebarViewEnum.TableOfContents;

  badgeCount: number = 0;

  CalculationFormSidebarViewEnum = CalculationFormSidebarViewEnum;

  errors: { property: string, messages: string[], value: any }[] = [];

  // todo: listen to form modfiication event and re-init the list of headings

  currentForm: AdvancedForm<any>;

  currentLocale: Locale;

  eventSubscription: Subscription;
  currentTimeout;

  constructor(private elRef: ElementRef,
              private readonly formChangeManager: FormChangeManager,
              private readonly calculationStore: CalculationStore,
              private readonly activatedRoute: ActivatedRoute,
              private readonly localeService: LocaleService,
              private readonly debugUtils: DebugUtils,
              private readonly eventManager: EventManager,
  ) {
  }

  get showPropertyPath() {
    return this.debugUtils.isDebug();
  }

  async ngOnInit() {
    this.formChangeSubscription = this.formChangeManager.formChange.subscribe(value => {
      this.currentForm = value;

      // Let the DOM update.
      if (this.currentTimeout === undefined) {
        this.currentTimeout = setTimeout(() => {
          this.refreshSidebar();
          this.currentTimeout = undefined;
        }, 500);
      }
    });

    this.currentLocale = await this.localeService.getLocaleForUser();

    // todo: Ideally this would be passed as a parameter
    this.activatedRoute.parent.params.subscribe(params => {
      if (params.calculationId === undefined) {
        return;
      }

      this.calculationSubscription = this.calculationStore.getErrors(params.calculationId).subscribe(validationErrors => {
        this.errors = validationErrors.filter(error => error.messages.length > 0);
        this.badgeCount = this.errors.length ?? 0;

        if (this.errors.length > 0) {
          this.view = CalculationFormSidebarViewEnum.Errors;

          // Open the sidebar if there are errors
          this.openSidebar();
        }
      });
    });

    this.eventSubscription = this.eventManager.eventStream.subscribe(value => {
      if (value === CalculationEventEnum.ErrorsBadgeClicked) {
        this.view = CalculationFormSidebarViewEnum.Errors;

        this.openSidebar();
      }
    });
  }

  closeSidebar() {
    this.status = CalculationFormSidebarStatusEnum.Closed;
  }

  ngOnDestroy() {
    this.formChangeSubscription.unsubscribe();
  }

  navigateTo(view: CalculationFormSidebarViewEnum) {
    this.view = view;
  }

  getLabelTitle(path: string): string {
    if (this.currentForm === undefined) {
      return '';
    }

    // The errors list is only refreshed on validate/save, so right after a child (or any
    // array item) is deleted it can still reference an index that no longer exists
    // (e.g. "...children.4.firstName" once there are only 4 children left). Walking the
    // live form tree for that out-of-range path throws inside this template binding, which
    // aborts change detection and freezes the page. Fall back to the raw path instead.
    let metadataOptions;
    try {
      metadataOptions = this.currentForm.getMetadataOptionsAtPath(path);
    } catch {
      return path;
    }

    if (metadataOptions && metadataOptions.hasOwnProperty('labelTitle')) {
      if (metadataOptions['labelTitle'].hasOwnProperty(this.currentLocale)) {
        return metadataOptions['labelTitle'][this.currentLocale];
      } else if (metadataOptions['labelTitle'].hasOwnProperty('en')) {
        return metadataOptions['labelTitle']['en'];
      }
    }
    return path;
  }

  highlightTarget(id: string) {
    // The targeted element may no longer be in the DOM (e.g. the error points at a child
    // that was just deleted), so guard against a null dereference.
    const element = document.getElementById(id);
    if (element === null) {
      return;
    }

    element.classList.remove('target');
    element.classList.add('target');

    setTimeout(() => {
      document.getElementById(id)?.classList.remove('target');
    }, 3000);
  }

  iterateHeadings(nextHeadings: any[], index: number, parent?: Heading) {
    const htmlHeading = nextHeadings[index];

    let nestingLevel = 0;

    let tempParent = parent;

    while (tempParent !== undefined) {
      nestingLevel++;
      tempParent = tempParent.parent;
    }

    const heading: Heading = {
      id: htmlHeading.id,
      title: htmlHeading.innerText,
      element: htmlHeading,
      children: [],
      parent: parent,
      nestingLevel,
    };

    // If there's a parent specified, we assign us as a children.
    if (parent) {
      parent.children.push(heading);
    } else {
      this.headings.push(heading);
    }

    const nextIndex = index + 1;

    if (nextIndex >= nextHeadings.length) {
      return;
    }

    // Now retrieve the parent for the next heading.

    // If the next heading is a H1, don't pass any parent
    if (nextHeadings[nextIndex].nodeName === 'H1') {
      return this.iterateHeadings(nextHeadings, nextIndex);
    }

    const currentNestingLevel = parseInt(htmlHeading.nodeName[1]);
    const nextNestingLevel = parseInt(nextHeadings[nextIndex].nodeName[1]);

    if (currentNestingLevel === nextNestingLevel) {
      return this.iterateHeadings(nextHeadings, nextIndex, parent);
    } else if (currentNestingLevel > nextNestingLevel) {
      return this.iterateHeadings(nextHeadings, nextIndex, heading);
    } else {
      // The nextNestingLevel is lower than current, therefore we find the parent of our parent X times. X being the difference between the two levels.
      // We could not need the condition below since it would automatically work but would make the code way more complicate to understand.
      let nextParent = heading;
      for (let i = currentNestingLevel; i > nextNestingLevel; i--) {
        nextParent = nextParent.parent;
      }

      return this.iterateHeadings(nextHeadings, nextIndex, nextParent);
    }
  }

  openSidebar() {
    this.status = CalculationFormSidebarStatusEnum.Open;

    this.refreshSidebar();
  }

  refreshSidebar() {
    const parentElement = this.elRef.nativeElement.parentElement;
    const form = parentElement.querySelector('form');
    const htmlHeadings = form.querySelectorAll('h1, h2, h3');

    this.headings = [];
    this.iterateHeadings(htmlHeadings, 0);
  }
}
